import { useEffect, useRef } from "react";
import api from "@/lib/api";

// Poll interval for background version checks (only while the tab is visible).
const POLL_MS = 5 * 60 * 1000; // 5 minutes

async function purgeClientCaches() {
  // Clear any Service Worker caches. Silently ignore browsers that don't
  // implement the API (e.g. older Safari).
  try {
    if (typeof caches !== "undefined" && caches.keys) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch { /* ignore */ }
  // Unregister any leftover Service Workers so they can't keep serving
  // stale bundles across the reload.
  try {
    if (typeof navigator !== "undefined" && navigator.serviceWorker?.getRegistrations) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch { /* ignore */ }
}

/**
 * Watches `/api/version` for a change in the deployed `build_id`. On the very
 * first successful call, we memorise whatever the server reports. Every
 * subsequent call that returns a different `build_id` triggers a cache purge
 * + full page reload so the user lands on the newest bundle automatically.
 *
 * The check is bypassed entirely when the server reports "unversioned" so
 * local dev / preview environments never thrash on backend restarts.
 */
export function useAutoReloadOnNewBuild() {
  const seenRef = useRef(null);
  const reloadingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      if (reloadingRef.current) return;
      try {
        const { data } = await api.get("/version");
        const id = data?.build_id;
        if (!id || id === "unversioned") return; // dev/preview — skip
        if (cancelled) return;
        if (seenRef.current == null) {
          seenRef.current = id;
          return;
        }
        if (id !== seenRef.current) {
          reloadingRef.current = true;
          await purgeClientCaches();
          // `location.reload()` is enough on modern browsers — the newly
          // fetched index.html points at the new hashed bundles.
          window.location.reload();
        }
      } catch { /* offline / auth expired — retry next tick */ }
    };

    // Initial probe right away, then a lightweight poll while the tab is
    // visible. Also re-check whenever the user returns to the tab, which is
    // the most common trigger in practice.
    check();
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") check();
    }, POLL_MS);
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);
}
